<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import type { QuestionsAccordionEmits, QuestionsAccordionProps } from '.';
import type {
	FAQPage,
	Question as QuestionJsonLd,
	WithContext
} from 'schema-dts';

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@/components/ui/accordion';
import { cn } from '@/utils';
import { type AccordionRootEmits } from 'reka-ui';

const props = defineProps<QuestionsAccordionProps>();
const emit = defineEmits<QuestionsAccordionEmits>();

const defaultValue = defineModel<string>();
const fallbackDefaultValue = computed(
	() => props.questions?.[0]?.question || ''
);

const questionListJsonLd = computed<WithContext<FAQPage> | null>(() => {
	if (!props.questions?.length) return null;

	return {
		'@type': 'FAQPage',
		'@context': 'https://schema.org',
		mainEntity: props.questions.map<QuestionJsonLd>((question, index) => ({
			'@type': 'Question',
			position: index + 1,
			name: question.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: question.answer
			}
		}))
	} satisfies WithContext<FAQPage>;
});

function handleAccordionClick(question: string, position: number) {
	emit('question-clicked', {
		question,
		position
	});
}

useJsonld(() => questionListJsonLd.value);
const accordionValue = ref(defaultValue.value);
function handleUpdateModelValue(
	value: AccordionRootEmits['update:modelValue'][0]
) {
	if (!value) return;
	const normalizedValue = (Array.isArray(value) ? value[0] : value) ?? '';
	const index =
		props.questions?.findIndex(q => q.question === normalizedValue) ?? 0;
	handleAccordionClick(normalizedValue, index + 1);
}
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<Accordion
		v-model="accordionValue"
		type="single"
		class="w-full"
		collapsible
		:default-value="defaultValue || fallbackDefaultValue"
		@update:model-value="handleUpdateModelValue"
	>
		<AccordionItem
			v-for="question in questions"
			:key="question.question"
			:value="question.question"
			:class="
				cn(
					'border-white-dark-2 text-primary no-underline',
					props.accordionItemClass
				)
			"
		>
			<AccordionTrigger
				class="gap-3 border-b border-white-dark-2 py-4 pl-4 pr-3 text-left font-matter-sq text-base font-normal leading-normal tracking-[-0.32px] text-primary md:p-6 md:text-lg"
				:accordion-header-as="accordionTriggerAs"
			>
				{{ question.question }}
			</AccordionTrigger>

			<AccordionContent
				class="p-4 text-sm font-normal leading-[175%] text-primary md:px-11 md:py-8 md:text-base"
			>
				<p v-html="question.answer"></p>
			</AccordionContent>
		</AccordionItem>
	</Accordion>
</template>
